import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, X, CurrencyInr, Scales, ArrowRight, Check } from '@phosphor-icons/react';
import { BUDGET_CATEGORIES, fmtMoney } from './TripCard';

export default function BudgetTab({ api, trip }) {
  const [items, setItems] = useState([]);
  const [totalPlanned, setTotalPlanned] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [showSettle, setShowSettle] = useState(false);
  const [settleSummary, setSettleSummary] = useState(null);

  const travellers = Array.isArray(trip.travellers) ? trip.travellers : [];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/trips/${trip.trip_id}/budget`);
      setItems(r.data?.items || []);
      setTotalPlanned(r.data?.total_planned || 0);
      setTotalSpent(r.data?.total_spent || 0);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api, trip.trip_id]);

  const fetchSettle = useCallback(async () => {
    if (travellers.length < 2) { setSettleSummary(null); return; }
    try {
      const r = await api.get(`/trips/${trip.trip_id}/budget/settle-up`);
      setSettleSummary(r.data || null);
    } catch (_) { setSettleSummary(null); }
  }, [api, trip.trip_id, travellers.length]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchSettle(); }, [fetchSettle, items]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget item?')) return;
    try {
      await api.delete(`/trips/${trip.trip_id}/budget/${id}`);
      await fetchItems();
    } catch (_) { /* ignore */ }
  };

  const cur = trip.currency || 'INR';
  const budget = Number(trip.budget_amount || 0);
  const remaining = budget - totalSpent;
  const usagePct = budget > 0 ? Math.min(100, Math.round((totalSpent / budget) * 100)) : 0;

  const byCat = {};
  items.forEach(it => {
    const k = it.category || 'misc';
    if (!byCat[k]) byCat[k] = { planned: 0, spent: 0, items: [] };
    byCat[k].planned += Number(it.planned || 0);
    byCat[k].spent += Number(it.spent || 0);
    byCat[k].items.push(it);
  });

  const transferCount = settleSummary?.transfers?.length || 0;

  return (
    <div className="space-y-4" data-testid="budget-tab">
      {/* Summary */}
      <div className="rounded-2xl p-4 sm:p-5"
        style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.10), rgba(245,158,11,0.02))', border: '1px solid rgba(245,158,11,0.25)' }}
        data-testid="budget-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <Stat label="Trip budget" value={fmtMoney(budget, cur)} color="#F59E0B" testId="budget-stat-total" />
          <Stat label="Planned" value={fmtMoney(totalPlanned, cur)} color="#3B82F6" testId="budget-stat-planned" />
          <Stat label="Spent" value={fmtMoney(totalSpent, cur)} color="#EF4444" testId="budget-stat-spent" />
          <Stat label="Remaining" value={fmtMoney(remaining, cur)} color={remaining >= 0 ? '#22C55E' : '#EF4444'} testId="budget-stat-remaining" />
        </div>
        {budget > 0 && (
          <div>
            <div className="flex justify-between text-[10px] text-neutral-500 mb-1">
              <span>Budget usage</span>
              <span className="font-bold">{usagePct}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usagePct}%`,
                  background: usagePct >= 100 ? '#EF4444' : usagePct >= 80 ? '#F59E0B' : '#22C55E',
                }} />
            </div>
          </div>
        )}
      </div>

      {/* Settle-up chip */}
      {travellers.length >= 2 && (
        <button onClick={() => setShowSettle(true)} data-testid="settle-up-chip"
          className="w-full rounded-xl px-4 py-3 flex items-center justify-between gap-3 hover:scale-[1.005] active:scale-[0.995] transition"
          style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.06))', border: '1px solid rgba(99,102,241,0.30)' }}>
          <div className="flex items-center gap-2.5 min-w-0">
            <Scales size={18} weight="duotone" className="text-indigo-300 flex-shrink-0" />
            <div className="min-w-0 text-left">
              <p className="text-[12px] font-bold text-white truncate">Split between {travellers.length} travellers</p>
              <p className="text-[10px] text-neutral-400 truncate" data-testid="settle-up-chip-summary">
                {transferCount === 0
                  ? 'All square. No transfers needed.'
                  : `${transferCount} transfer${transferCount > 1 ? 's' : ''} suggested · tap to settle up`}
              </p>
            </div>
          </div>
          <ArrowRight size={14} className="text-indigo-300 flex-shrink-0" />
        </button>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-neutral-400">{items.length} line item(s)</p>
        <button onClick={() => setShowForm({})} data-testid="budget-add-btn"
          className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[11px] font-bold hover:bg-[#E85D2A] flex items-center gap-1.5">
          <Plus size={11} weight="bold" /> Add expense
        </button>
      </div>

      {loading && <div className="text-center py-6 text-sm text-neutral-500">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(245,158,11,0.05)', border: '1px dashed rgba(245,158,11,0.30)' }}
          data-testid="budget-empty">
          <CurrencyInr size={28} weight="duotone" className="text-amber-400 mx-auto mb-2" />
          <p className="text-sm text-neutral-300 mb-1">No expenses tracked yet.</p>
          <p className="text-[11px] text-neutral-500">Add planned costs (hotel, flights, activities) and update `spent` as you go.</p>
          {travellers.length < 2 && (
            <p className="text-[10px] text-neutral-500 mt-2 italic">Tip: add travellers in the trip details to split expenses between them.</p>
          )}
        </div>
      )}

      {!loading && Object.keys(byCat).length > 0 && (
        <div className="space-y-3">
          {BUDGET_CATEGORIES.filter(c => byCat[c.id]).map(c => (
            <div key={c.id} className="rounded-2xl overflow-hidden" data-testid={`budget-cat-${c.id}`}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="px-4 py-2.5 flex items-center justify-between gap-2"
                style={{ background: `${c.color}15`, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.color }}>{c.label}</span>
                <div className="flex items-center gap-3 text-[10px] font-bold">
                  <span className="text-blue-300">Planned {fmtMoney(byCat[c.id].planned, cur)}</span>
                  <span className="text-red-300">Spent {fmtMoney(byCat[c.id].spent, cur)}</span>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {byCat[c.id].items.map(it => {
                  const splitWith = Array.isArray(it.split_with) ? it.split_with : [];
                  return (
                    <div key={it.item_id} className="px-4 py-2.5 flex items-center gap-3 group hover:bg-white/[0.02]"
                      data-testid={`budget-item-${it.item_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{it.label}</p>
                        {it.notes && <p className="text-[11px] text-neutral-500 mt-0.5">{it.notes}</p>}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {it.paid_by && <p className="text-[10px] text-amber-300">Paid by: <b>{it.paid_by}</b></p>}
                          {splitWith.length > 0 && (
                            <p className="text-[10px] text-indigo-300" data-testid={`budget-split-${it.item_id}`}>
                              Split between {splitWith.length}: {splitWith.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-[10px] text-neutral-500">P {fmtMoney(it.planned, cur)}</p>
                          <p className="text-[11px] font-bold text-emerald-300">S {fmtMoney(it.spent, cur)}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button onClick={() => setShowForm(it)} data-testid={`budget-edit-${it.item_id}`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-neutral-300 hover:bg-white/10">Edit</button>
                          <button onClick={() => handleDelete(it.item_id)} data-testid={`budget-delete-${it.item_id}`}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-500 hover:bg-red-500/10 hover:text-red-400">
                            <Trash size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <BudgetEditModal api={api} trip={trip} item={showForm.item_id ? showForm : null}
          travellers={travellers}
          onClose={() => setShowForm(null)}
          onSaved={async () => { setShowForm(null); await fetchItems(); }} />
      )}

      {showSettle && (
        <SettleUpModal api={api} trip={trip}
          onClose={() => setShowSettle(false)} />
      )}
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div className="rounded-xl p-2.5" data-testid={testId} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[9px] uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="text-base sm:text-lg font-bold mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function BudgetEditModal({ api, trip, item, travellers, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: item?.category || 'misc',
    label: item?.label || '',
    planned: item?.planned ?? 0,
    spent: item?.spent ?? 0,
    paid_by: item?.paid_by || '',
    notes: item?.notes || '',
    split_with: Array.isArray(item?.split_with) ? item.split_with : [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleSplit = (name) => {
    setForm(f => {
      const exists = f.split_with.includes(name);
      return { ...f, split_with: exists ? f.split_with.filter(n => n !== name) : [...f.split_with, name] };
    });
  };

  const splitEveryone = () => {
    setForm(f => ({ ...f, split_with: [...travellers] }));
  };

  const splitClear = () => {
    setForm(f => ({ ...f, split_with: [] }));
  };

  const save = async () => {
    setError('');
    if (!form.label.trim()) { setError('Label required'); return; }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        label: form.label,
        planned: Number(form.planned) || 0,
        spent: Number(form.spent) || 0,
        paid_by: form.paid_by,
        notes: form.notes,
        split_with: form.split_with,
      };
      if (item) await api.put(`/trips/${trip.trip_id}/budget/${item.item_id}`, payload);
      else await api.post(`/trips/${trip.trip_id}/budget`, payload);
      onSaved && onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const cur = trip.currency || 'INR';
  const splitCount = form.split_with.length;
  const sharePerPerson = splitCount > 0 ? Number(form.spent || 0) / splitCount : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="budget-edit-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">{item ? 'Edit expense' : 'Add expense'}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
          <div>
            <L>Category</L>
            <div className="flex flex-wrap gap-1.5">
              {BUDGET_CATEGORIES.map(c => (
                <button key={c.id} type="button" onClick={() => setForm(f => ({ ...f, category: c.id }))}
                  data-testid={`bud-cat-${c.id}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${form.category === c.id ? 'text-white' : 'text-neutral-400'}`}
                  style={form.category === c.id
                    ? { background: `${c.color}25`, borderColor: c.color, color: c.color }
                    : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.10)' }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <L>Label</L>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              data-testid="bud-label" className="t-input" placeholder="Hotel Taj, AI flight, Uber ride…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>Planned ({cur})</L>
              <input type="number" min="0" step="100" value={form.planned}
                onChange={e => setForm(f => ({ ...f, planned: e.target.value }))}
                data-testid="bud-planned" className="t-input" />
            </div>
            <div>
              <L>Spent ({cur})</L>
              <input type="number" min="0" step="100" value={form.spent}
                onChange={e => setForm(f => ({ ...f, spent: e.target.value }))}
                data-testid="bud-spent" className="t-input" />
            </div>
          </div>
          <div>
            <L>Paid by (optional)</L>
            {travellers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setForm(f => ({ ...f, paid_by: '' }))}
                  data-testid="bud-paidby-none"
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${!form.paid_by ? 'bg-white/10 border-white/30 text-white' : 'bg-white/5 border-white/10 text-neutral-400'}`}>
                  —
                </button>
                {travellers.map(t => (
                  <button key={t} type="button" onClick={() => setForm(f => ({ ...f, paid_by: t }))}
                    data-testid={`bud-paidby-${t.replace(/\s+/g,'-').toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${form.paid_by === t ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'bg-white/5 border-white/10 text-neutral-400'}`}>
                    {t}
                  </button>
                ))}
              </div>
            ) : (
              <input value={form.paid_by} onChange={e => setForm(f => ({ ...f, paid_by: e.target.value }))}
                data-testid="bud-paidby" className="t-input" placeholder="Me / Spouse / Card-XYZ" />
            )}
          </div>

          {travellers.length >= 2 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <L>Split between</L>
                <div className="flex gap-1.5">
                  <button type="button" onClick={splitEveryone} data-testid="bud-split-all"
                    className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30 font-bold">Everyone</button>
                  <button type="button" onClick={splitClear} data-testid="bud-split-clear"
                    className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-neutral-300 hover:bg-white/10 font-bold">Clear</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {travellers.map(t => {
                  const on = form.split_with.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleSplit(t)}
                      data-testid={`bud-split-${t.replace(/\s+/g,'-').toLowerCase()}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold border inline-flex items-center gap-1 ${on ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-200' : 'bg-white/5 border-white/10 text-neutral-400'}`}>
                      {on && <Check size={9} weight="bold" />} {t}
                    </button>
                  );
                })}
              </div>
              {splitCount > 0 && Number(form.spent) > 0 && (
                <p className="text-[10px] text-indigo-300 mt-1.5" data-testid="bud-split-preview">
                  {fmtMoney(sharePerPerson, cur)} per person × {splitCount} = {fmtMoney(sharePerPerson * splitCount, cur)}
                </p>
              )}
            </div>
          )}

          <div>
            <L>Notes</L>
            <textarea rows="2" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="bud-notes" className="t-input" />
          </div>
        </div>
        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="bud-save-btn"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold disabled:opacity-50">
            {saving ? 'Saving…' : (item ? 'Save' : 'Add')}
          </button>
        </div>
        <style>{`.t-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.t-input:focus{border-color:#FF6B35}`}</style>
      </div>
    </div>
  );
}

function SettleUpModal({ api, trip, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/trips/${trip.trip_id}/budget/settle-up`);
        setData(r.data);
      } catch (e) { setError(e.response?.data?.detail || 'Settle-up failed'); }
      setLoading(false);
    })();
  }, [api, trip.trip_id]);

  const cur = data?.currency || trip.currency || 'INR';
  const balances = data?.balances || [];
  const transfers = data?.transfers || [];
  const untracked = data?.untracked_spent || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="settle-up-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scales size={16} weight="duotone" className="text-indigo-300" />
            <h3 className="text-sm font-bold text-white">Settle Up · {trip.title}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {loading && <div className="text-center py-6 text-sm text-neutral-500">Crunching numbers…</div>}
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}

          {!loading && !error && (
            <>
              {/* Balances */}
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Net balances</p>
                {balances.length === 0 ? (
                  <p className="text-sm text-neutral-500 italic">No traveller activity yet.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="settle-balances">
                    {balances.map(b => (
                      <div key={b.name} className="flex items-center justify-between rounded-xl px-3 py-2"
                        data-testid={`settle-balance-${b.name.replace(/\s+/g,'-').toLowerCase()}`}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white truncate">{b.name}</p>
                          <p className="text-[10px] text-neutral-500">
                            Paid {fmtMoney(b.paid, cur)} · Share {fmtMoney(b.owes, cur)}
                          </p>
                        </div>
                        <p className={`text-sm font-extrabold ${b.net > 0.01 ? 'text-emerald-300' : b.net < -0.01 ? 'text-red-300' : 'text-neutral-400'}`}>
                          {b.net > 0.01 ? '+' : ''}{fmtMoney(b.net, cur)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transfers */}
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Suggested transfers</p>
                {transfers.length === 0 ? (
                  <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.20)' }}
                    data-testid="settle-all-square">
                    <p className="text-sm font-bold text-emerald-300">✅ All square!</p>
                    <p className="text-[11px] text-neutral-400 mt-0.5">No transfers needed.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="settle-transfers">
                    {transfers.map((t, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl px-3 py-2.5"
                        data-testid={`settle-transfer-${i}`}
                        style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.10), rgba(168,85,247,0.05))', border: '1px solid rgba(99,102,241,0.30)' }}>
                        <div className="flex items-center gap-2 text-[13px] min-w-0">
                          <span className="font-bold text-red-300 truncate">{t.from}</span>
                          <ArrowRight size={12} className="text-neutral-500 flex-shrink-0" />
                          <span className="font-bold text-emerald-300 truncate">{t.to}</span>
                        </div>
                        <p className="text-sm font-extrabold text-white flex-shrink-0">{fmtMoney(t.amount, cur)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {untracked > 0.01 && (
                <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.20)' }}
                  data-testid="settle-untracked">
                  <p className="text-[11px] text-amber-200">
                    <b>{fmtMoney(untracked, cur)}</b> in expenses are untracked (no payer or split). Edit those rows to include them in the split.
                  </p>
                </div>
              )}

              <p className="text-[10px] text-neutral-500 italic text-center pt-1">
                Tip: edit any expense and tap &quot;Everyone&quot; under Split between to apply it to all travellers.
              </p>
            </>
          )}
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold hover:bg-[#E85D2A]"
            data-testid="settle-close-btn">Done</button>
        </div>
      </div>
    </div>
  );
}

function L({ children }) {
  return <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">{children}</label>;
}
